import { type ApiResponse, post } from "@src/appV2/api/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logError } from "@src/appV2/lib/analytics";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import axios, { type AxiosError } from "axios";
import { z } from "zod";

const postReviewRequestSchema = z.object({
  anonymous: z.boolean(),
  reviewDate: z.string().datetime().optional(),
  answers: z.array(
    z.object({
      questionId: z.string(),
      text: z.string().optional(),
      optionId: z.string().optional(),
      type: z.enum(["VIDEO", "VOICE", "WRITTEN"]).optional(),
      mediaFileKey: z.string().optional(),
      mediaMimeType: z.string().optional(),
      mediaDurationSeconds: z.number().optional(),
      mediaFileSizeBytes: z.number().optional(),
    })
  ),
  employmentVerificationFileKey: z.string().optional(),
});

const postReviewResponseSchema = z.object({
  data: z.object({
    type: z.string(),
    attributes: z.object({ count: z.number() }),
  }),
});

export type PostReviewRequest = z.infer<typeof postReviewRequestSchema>;
export type PostReviewResponse = z.infer<typeof postReviewResponseSchema>;

type PostReviewData = Omit<PostReviewRequest, "employmentVerificationFileKey"> & {
  employmentVerificationFile?: File | undefined;
};

const presignedUploadUrlResponseSchema = z.object({
  data: z.object({
    uploadUrl: z.string(),
    fileKey: z.string(),
  }),
});

async function fetchPresignedUrl(workplaceId: string) {
  const response = await post({
    url: `${environmentConfig.REACT_APP_SHIFT_REVIEW_API_URL}/workplaces/${workplaceId}/employment-verifications/presigned-upload-url`,
    requestSchema: z.undefined(),
    responseSchema: presignedUploadUrlResponseSchema,
  });

  return response.data.data;
}

export function usePostReview(
  workplaceId: string,
  options: UseMutationOptions<ApiResponse<PostReviewResponse>, AxiosError, PostReviewData> = {}
): UseMutationResult<ApiResponse<PostReviewResponse>, AxiosError, PostReviewData> {
  return useMutation({
    mutationFn: async (data: PostReviewData) => {
      const { employmentVerificationFile, ...rest } = data;

      let employmentVerificationFileKey: string | undefined;
      if (employmentVerificationFile) {
        let uploadUrl: string;
        let fileKey: string;

        try {
          ({ uploadUrl, fileKey } = await fetchPresignedUrl(workplaceId));
        } catch (error) {
          logError(APP_V2_APP_EVENTS.EMPLOYMENT_VERIFICATION_PRESIGNED_URL_FAILURE, { error });
          throw error;
        }

        try {
          await axios.put(uploadUrl, employmentVerificationFile, {
            headers: {
              "Content-Type": employmentVerificationFile.type,
            },
          });
        } catch (error) {
          logError(APP_V2_APP_EVENTS.EMPLOYMENT_VERIFICATION_FILE_UPLOAD_FAILURE, { error });
          throw error;
        }

        employmentVerificationFileKey = fileKey;
      }

      return await post({
        url: `${environmentConfig.REACT_APP_SHIFT_REVIEW_API_URL}/workplaces/${workplaceId}/reviews`,
        data: {
          ...rest,
          ...(employmentVerificationFileKey ? { employmentVerificationFileKey } : {}),
        },
        requestSchema: postReviewRequestSchema,
        responseSchema: postReviewResponseSchema,
      });
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.POST_REVIEW_FAILURE,
    },
    ...options,
  });
}
